import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ShieldCheck, CheckCircle2, ArrowLeft, ChevronLeft, ChevronRight, Sparkles, Navigation } from "lucide-react";
import api, { imageUrl } from "../lib/api";
import { EnquiryForm } from "../components/EnquiryForm";
import Seo from "../components/Seo";

const Section = ({ id, eyebrow, title, children }) => (
  <section id={id} className="py-14 border-t border-stone-200">
    {eyebrow && <span className="text-xs font-bold uppercase tracking-[0.25em] text-brand">{eyebrow}</span>}
    {title && <h2 className="font-heading text-3xl sm:text-4xl text-stone-900 mt-2 mb-8">{title}</h2>}
    {children}
  </section>
);

const ProjectDetail = () => {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    setP(null); setNotFound(false); setActive(0);
    api.get(`/projects/${slug}`).then((r) => setP(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-5 py-32 text-center">
      <h1 className="font-heading text-3xl text-stone-900">Project not found</h1>
      <Link to="/projects" className="inline-block mt-6 rounded-full bg-brand text-white px-6 py-3 text-sm font-semibold">All projects</Link>
    </div>
  );
  if (!p) return <div className="max-w-7xl mx-auto px-5 py-20"><div className="h-96 rounded-2xl bg-stone-100 animate-pulse" /></div>;

  const gallery = (p.gallery?.length ? p.gallery : [p.hero_image]).filter(Boolean).map(imageUrl);
  const mapQuery = encodeURIComponent(`${p.location}, Rajasthan, India`);

  return (
    <div>
      <Seo
        title={`${p.title} — ${p.location} | Satyam Real Estate`}
        description={`${p.tagline} ${p.overview_text?.slice(0, 120) || ""}`}
        image={imageUrl(p.hero_image)}
        jsonLd={{ "@context": "https://schema.org", "@type": "ApartmentComplex", name: p.title, description: p.tagline, address: p.location }}
      />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end">
        {p.hero_image ? <img src={imageUrl(p.hero_image)} alt={p.title} className="absolute inset-0 w-full h-full object-cover" />
          : <div className="absolute inset-0 bg-stone-900" />}
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950/90 via-stone-950/40 to-stone-950/20" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full pb-12">
          <Link to="/projects" className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white mb-5"><ArrowLeft size={16} /> All projects</Link>
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="rounded-full bg-brand text-white text-xs font-semibold px-3 py-1">{p.status}</span>
            {p.rera_id && <span className="rounded-full bg-white/90 text-stone-800 text-xs font-semibold px-3 py-1 flex items-center gap-1"><ShieldCheck size={13} /> RERA: {p.rera_id}</span>}
          </div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-heading text-4xl sm:text-6xl text-white max-w-3xl leading-tight">{p.title}</motion.h1>
          <p className="flex items-center gap-1.5 text-white/85 mt-3"><MapPin size={16} /> {p.location}</p>
          <p className="text-white/85 mt-3 max-w-2xl">{p.tagline}</p>
          <div className="flex gap-3 mt-7">
            <a href="#enquiry" className="rounded-full bg-brand text-white px-6 py-3 text-sm font-semibold hover:bg-brand-dark transition-colors">Enquire Now</a>
            <a href="#overview" className="rounded-full bg-white/10 border border-white/25 text-white px-6 py-3 text-sm font-semibold hover:bg-white/20 transition-colors">Explore Project</a>
          </div>
        </div>
      </section>

      {/* Stats */}
      {p.stats?.length > 0 && (
        <div className="bg-stone-950 text-white">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10" data-testid="project-stats">
            {p.stats.map((s) => (
              <div key={s.label} className="py-8 px-4 text-center">
                <p className="font-heading text-3xl text-brand-light">{s.value}</p>
                <p className="text-xs text-stone-400 mt-1 uppercase tracking-wider">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {/* Overview */}
        <Section id="overview" eyebrow="About the Project" title={p.overview_heading || "Overview"}>
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <p className="text-stone-600 leading-relaxed whitespace-pre-line">{p.overview_text}</p>
            {p.highlights?.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-3">
                {p.highlights.map((h) => (
                  <div key={h} className="flex items-start gap-2 text-sm text-stone-700 bg-stone-50 rounded-xl p-3">
                    <Sparkles size={16} className="text-brand mt-0.5 shrink-0" /> {h}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Section>

        {/* Video */}
        {p.video_url && (
          <Section id="video" eyebrow="Watch" title="Project Film">
            <div className="rounded-2xl overflow-hidden border border-stone-200 aspect-video bg-black" data-testid="project-video">
              <iframe title={`${p.title} video`} width="100%" height="100%" src={p.video_url}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen style={{ border: 0 }} />
            </div>
          </Section>
        )}

        {/* Features */}
        {p.features?.length > 0 && (
          <Section id="features" eyebrow="Why Choose" title="Key Features">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="project-features">
              {p.features.map((f) => (
                <div key={f.title} className="rounded-2xl border border-stone-200 p-6 hover:border-brand transition-colors">
                  <div className="w-11 h-11 rounded-xl bg-accent text-brand grid place-items-center mb-4"><CheckCircle2 size={20} /></div>
                  <h3 className="font-semibold text-stone-900">{f.title}</h3>
                  <p className="text-sm text-stone-500 mt-2 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Amenities */}
        {p.amenities?.length > 0 && (
          <Section id="amenities" eyebrow="Lifestyle" title="Amenities">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="project-amenities">
              {p.amenities.map((a) => (
                <div key={a} className="flex items-center gap-2 text-sm text-stone-700 rounded-xl border border-stone-200 px-4 py-3">
                  <CheckCircle2 size={16} className="text-brand shrink-0" /> {a}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Facilities */}
        {p.facilities?.length > 0 && (
          <Section id="facilities" eyebrow="Infrastructure" title="Facilities & Infrastructure">
            <div className="flex flex-wrap gap-2.5">
              {p.facilities.map((f) => (
                <span key={f} className="rounded-full bg-stone-100 text-stone-700 text-sm px-4 py-2">{f}</span>
              ))}
            </div>
          </Section>
        )}

        {/* Configurations */}
        {p.configs?.length > 0 && (
          <Section id="plans" eyebrow="Configurations" title="Available Configurations">
            <div className="rounded-2xl border border-stone-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-stone-500 bg-stone-50 border-b border-stone-200">
                    <th className="p-4 font-medium">Type</th>
                    <th className="p-4 font-medium">Size</th>
                    <th className="p-4 font-medium">Price</th>
                    <th className="p-4 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {p.configs.map((c, i) => (
                    <tr key={`${c.name}-${c.size}-${i}`} className="border-b border-stone-100 last:border-0">
                      <td className="p-4 font-medium text-stone-800">{c.name}</td>
                      <td className="p-4 text-stone-600">{c.size}</td>
                      <td className="p-4 text-stone-600">{c.price}</td>
                      <td className="p-4 text-right"><a href="#enquiry" className="text-brand font-semibold">Enquire</a></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {/* Gallery */}
        {gallery.length > 0 && (
          <Section id="gallery" eyebrow="Visual Tour" title="Project Gallery">
            <div className="relative rounded-2xl overflow-hidden bg-stone-100 aspect-[16/9]" data-testid="project-gallery">
              <img src={gallery[active]} alt={p.title} className="w-full h-full object-cover" />
              {gallery.length > 1 && (
                <>
                  <button onClick={() => setActive((active - 1 + gallery.length) % gallery.length)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-white/85 hover:bg-white transition-colors"><ChevronLeft /></button>
                  <button onClick={() => setActive((active + 1) % gallery.length)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-white/85 hover:bg-white transition-colors"><ChevronRight /></button>
                </>
              )}
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-3 mt-3 overflow-x-auto no-scrollbar">
                {gallery.map((im, i) => (
                  <button key={`${im}-${i}`} onClick={() => setActive(i)}
                    className={`shrink-0 w-24 h-20 rounded-lg overflow-hidden border-2 transition-colors ${active === i ? "border-brand" : "border-transparent"}`}>
                    <img src={im} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </Section>
        )}

        {/* Connectivity */}
        {p.connectivity?.length > 0 && (
          <Section id="location" eyebrow="Connectivity" title="Location & Connectivity">
            <div className="grid sm:grid-cols-2 gap-4">
              {p.connectivity.map((c) => (
                <div key={c.place} className="flex items-center justify-between rounded-xl border border-stone-200 px-5 py-4">
                  <span className="flex items-center gap-2 text-stone-700"><Navigation size={16} className="text-brand" /> {c.place}</span>
                  <span className="text-sm font-semibold text-stone-900">{c.time}</span>
                </div>
              ))}
            </div>
            <div className="rounded-2xl overflow-hidden border border-stone-200 mt-6">
              <iframe title="Project location" width="100%" height="340" loading="lazy" style={{ border: 0 }}
                src={`https://maps.google.com/maps?q=${mapQuery}&z=13&output=embed`} />
            </div>
          </Section>
        )}

        {/* Enquiry */}
        <Section id="enquiry" eyebrow="Get in Touch" title="Interested in this project?">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <p className="text-stone-600">Schedule a site visit, request detailed pricing, or ask us anything about <strong>{p.title}</strong>. Our team will get back to you shortly.</p>
              <div className="mt-6 space-y-2 text-sm text-stone-600">
                <p className="flex items-center gap-2"><MapPin size={16} className="text-brand" /> {p.location}</p>
                {p.developer && <p>Developed by <strong>{p.developer}</strong></p>}
                {p.rera_id && <p>RERA: {p.rera_id}</p>}
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <EnquiryForm property={{ title: `${p.title} (Project)`, slug: p.slug }} locality={p.location} />
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default ProjectDetail;
